// src/careComponents/pages/roomEditor.tsx
import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  MenuItem,
  Divider,
  Grid,
  Collapse,
} from "@mui/material";
import { Delete, Add, Notes } from "@mui/icons-material";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { toEnumRoomType } from "../../util/roomType";

type RoomEditorForm = {
  rooms: Array<{
    id?: number | string;
    roomNumber: string;
    roomType?: string;
    notes?: string;
    beds: Array<{
      id?: number | string;
      bedLabel?: string | null;
      gender?: "Male" | "Female" | "Any" | string;
      isOccupied?: boolean;
      notes?: string | null;
      hasPrivateBathroom?: boolean;
      hasHospitalBed?: boolean;
      hasHoyerLift?: boolean;
      hasOxygen?: boolean;
      accommodatesBariatric?: boolean;
    }>;
  }>;
};

// helper: show “Bed N” only in UI if no custom label
const displayBedLabel = (raw: string | null | undefined, idx: number) =>
  raw && raw.trim() ? raw.trim() : `Bed ${idx + 1}`;

type Props = {
  roomIdx: number;
  removeRoom: () => void;
};

export default function RoomEditor({ roomIdx, removeRoom }: Props) {
  const { control, watch } = useFormContext<RoomEditorForm>();

  // Beds field array for this room
  const bedsFA = useFieldArray({
    control,
    name: `rooms.${roomIdx}.beds`,
    keyName: "keyId",
  });

  const roomPath = `rooms.${roomIdx}` as const;

  // Watch entire room & its beds for UI toggles and safety checks
  const room = watch(roomPath) ?? {};
  const beds = (watch(`${roomPath}.beds`) as unknown[] | undefined) ?? [];

  // UI-only note toggles
  const [roomNotesOpen, setRoomNotesOpen] = React.useState(false);
  const [bedNotesOpen, setBedNotesOpen] = React.useState<Record<string, boolean>>({});
  const toggleBedNotes = (keyId: string) =>
    setBedNotesOpen((m) => ({ ...m, [keyId]: !m[keyId] }));


  // Add a new bed
  const addBed = () => {
    bedsFA.append({
      bedLabel: null,
      gender: "Any",
      isOccupied: false,
      notes: null,
      hasPrivateBathroom: false,
      hasHospitalBed: false,
      hasHoyerLift: false,
      hasOxygen: false,
      accommodatesBariatric: false,
    });
  };


  // Remove a bed; if it's the last bed, remove the whole room immediately
  const handleRemoveBed = (bedIdx: number) => {
    if (bedsFA.fields.length <= 1) {
      removeRoom();
    } else {
      bedsFA.remove(bedIdx);
    }
  };

  // Safety net: if the beds array becomes empty (by any means), remove the room
  React.useEffect(() => {
    if (Array.isArray(beds) && beds.length === 0) {
      removeRoom();
    }
  }, [beds.length, removeRoom]); // watch length only to avoid deep re-renders

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      {/* Room header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Room
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant={roomNotesOpen ? "contained" : "outlined"}
            startIcon={<Notes fontSize="small" />}
            onClick={() => setRoomNotesOpen((v) => !v)}
          >
          </Button>
          <IconButton size="small" onClick={removeRoom} aria-label="Remove room">
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Room fields */}
      <Grid container spacing={1.25} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${roomPath}.roomNumber`}
            control={control}
            defaultValue={room?.roomNumber ?? ""}
            render={({ field }) => (
              <TextField {...field} label="Room Number" size="small" fullWidth />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${roomPath}.roomType`}
            control={control}
            render={({ field }) => (
              <TextField
                select
                label="Room Type"
                size="small"
                fullWidth
                value={toEnumRoomType(field.value) ?? ""}
                onChange={(e) => field.onChange(toEnumRoomType(e.target.value))}
              >
                <MenuItem value="">Select…</MenuItem>
                <MenuItem value="PRIVATE">Private</MenuItem>
                <MenuItem value="SHARED">Shared</MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>

      {/* Room notes (collapsible) */}
      <Collapse in={roomNotesOpen} unmountOnExit>
        <Box sx={{ mb: 2 }}>
          <Controller
            name={`${roomPath}.notes`}
            control={control}
            defaultValue={room?.notes ?? ""}
            render={({ field }) => (
              <TextField
                {...field}
                label="Room Notes"
                size="small"
                fullWidth
                multiline
                minRows={3}
              />
            )}
          />
        </Box>
      </Collapse>

      <Divider sx={{ my: 1.25 }} />

      {/* Beds header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Beds
        </Typography>
      </Stack>

      {/* One full-width bed per row */}
      <Stack spacing={1.25}>
        {bedsFA.fields.map((f, bedIdx) => {
          const fieldKey = String((f as any).keyId);
          const base = `rooms.${roomIdx}.beds.${bedIdx}` as const;
          const bed = (watch(base) as any) ?? (f as any);
          const notesOpen = !!bedNotesOpen[fieldKey];

          return (
            <Paper
              key={fieldKey}
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 1.5, width: "100%" }}
            >
              {/* Bed card header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography fontWeight={800}>
                  {displayBedLabel(bed?.bedLabel ?? (f as any)?.bedLabel, bedIdx)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={notesOpen ? "contained" : "outlined"}
                    startIcon={<Notes fontSize="small" />}
                    onClick={() => toggleBedNotes(fieldKey)}
                  >
                  </Button>
                  <IconButton size="small" onClick={() => handleRemoveBed(bedIdx)} aria-label="Remove bed">
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Compact internal grid */}
              <Grid container spacing={1.25}>
                {/* Bed label */}
                <Grid item xs={12} md={4}>
                  <Controller
                    name={`${base}.bedLabel`}
                    control={control}
                    defaultValue={bed?.bedLabel ?? (f as any)?.bedLabel ?? ""}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Bed Label (optional)"
                        size="small"
                        fullWidth
                        placeholder={`e.g., ${displayBedLabel(
                          bed?.bedLabel ?? (f as any)?.bedLabel,
                          bedIdx
                        )}`}
                      />
                    )}
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={6} md={3}>
                  <Controller
                    name={`${base}.gender`}
                    control={control}
                    defaultValue={bed?.gender ?? (f as any)?.gender ?? "Any"}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Preference"
                        size="small"
                        fullWidth
                        value={field.value ?? "Any"}
                        onChange={(e) => field.onChange(e.target.value)}
                      >
                        <MenuItem value="Any">Any</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Occupied */}
                <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.isOccupied`}
                        control={control}
                        defaultValue={!!(bed?.isOccupied ?? (f as any)?.isOccupied)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Occupied"
                  />
                </Grid>

                {/* Bed notes (collapsible, full width) */}
                <Grid item xs={12}>
                  <Collapse in={notesOpen} unmountOnExit>
                    <Controller
                      name={`${base}.notes`}
                      control={control}
                      defaultValue={bed?.notes ?? (f as any)?.notes ?? ""}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Bed Notes"
                          size="small"
                          fullWidth
                          multiline
                          minRows={3}
                        />
                      )}
                    />
                    <Divider sx={{ mt: 1, mb: 1 }} />
                  </Collapse>
                </Grid>

                {/* Feature flags */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.hasPrivateBathroom`}
                        control={control}
                        defaultValue={!!(bed?.hasPrivateBathroom ?? (f as any)?.hasPrivateBathroom)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Private Bathroom"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.hasHospitalBed`}
                        control={control}
                        defaultValue={!!(bed?.hasHospitalBed ?? (f as any)?.hasHospitalBed)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Hospital Bed"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.hasHoyerLift`}
                        control={control}
                        defaultValue={!!(bed?.hasHoyerLift ?? (f as any)?.hasHoyerLift)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Hoyer Lift"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.hasOxygen`}
                        control={control}
                        defaultValue={!!(bed?.hasOxygen ?? (f as any)?.hasOxygen)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Oxygen"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name={`${base}.accommodatesBariatric`}
                        control={control}
                        defaultValue={!!(bed?.accommodatesBariatric ?? (f as any)?.accommodatesBariatric)}
                        render={({ field }) => (
                          <Checkbox
                            size="small"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Bariatric"
                  />
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Stack>

      {/* Add Bed button at bottom for quick entry */}
      <Box sx={{ mt: 1.25 }}>
        <Button startIcon={<Add />} variant="outlined" size="small" onClick={addBed}>
          Add Bed
        </Button>
      </Box>
    </Paper>
  );
}
